/**
 * Feature 073 — Upload form for a new PDF document.
 * Owns local file/displayName/description state and surfaces validation errors
 * inline. Hands the uploaded { fileKey, publicUrl, size } back to the parent.
 */

import { useState, type ChangeEvent } from 'react';
import { Loader2, Upload } from 'lucide-react';
import {
  uploadProviderDocumentBinary,
  type CreateProviderDocumentInput,
} from '../../api/providerDocumentsApi';

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_DISPLAY_NAME = 200;
export const MAX_DESCRIPTION = 50;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

type ProviderDocumentUploadFormProps = {
  isSaving: boolean;
  onSubmit: (input: CreateProviderDocumentInput) => Promise<void>;
};

export function ProviderDocumentUploadForm({
  isSaving,
  onSubmit,
}: ProviderDocumentUploadFormProps) {
  const [pendingDisplayName, setPendingDisplayName] = useState('');
  const [pendingDescription, setPendingDescription] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setError(null);
    if (!file) {
      setPendingFile(null);
      return;
    }
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are supported.');
      setPendingFile(null);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError(`PDFs must be 10 MB or smaller (got ${formatBytes(file.size)}).`);
      setPendingFile(null);
      return;
    }
    setPendingFile(file);
    if (!pendingDisplayName.trim()) {
      setPendingDisplayName(file.name.replace(/\.pdf$/i, ''));
    }
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    const displayName = pendingDisplayName.trim();
    if (!displayName) {
      setError('Please provide a display name.');
      return;
    }
    setError(null);
    try {
      const uploaded = await uploadProviderDocumentBinary(pendingFile);
      await onSubmit({
        fileKey: uploaded.fileKey,
        fileUrl: uploaded.publicUrl,
        fileSize: pendingFile.size,
        displayName: displayName.slice(0, MAX_DISPLAY_NAME),
        description: pendingDescription.trim().slice(0, MAX_DESCRIPTION) || null,
      });
      setPendingFile(null);
      setPendingDisplayName('');
      setPendingDescription('');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Upload failed. Please try again.',
      );
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h4 className="font-medium text-foreground">Upload a new PDF</h4>
      <p className="mt-1 text-sm text-muted-foreground">
        PDFs up to 10 MB. The display name and description are shown on the
        public profile.
      </p>
      <div className="mt-4 space-y-3">
        <input
          type="file"
          accept="application/pdf"
          aria-label="Choose PDF file"
          onChange={handleFileChange}
          className="block w-full text-sm"
        />
        <input
          type="text"
          value={pendingDisplayName}
          maxLength={MAX_DISPLAY_NAME}
          onChange={(e) => setPendingDisplayName(e.target.value)}
          placeholder="Display name (required)"
          aria-label="Display name"
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={pendingDescription}
          maxLength={MAX_DESCRIPTION}
          onChange={(e) => setPendingDescription(e.target.value)}
          placeholder={`Short description (≤${MAX_DESCRIPTION} chars, optional)`}
          aria-label="Description"
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <button
          type="button"
          onClick={handleUpload}
          disabled={!pendingFile || isSaving}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          Upload PDF
        </button>
      </div>
    </div>
  );
}
